import { useEffect, useState } from "react";
import { Bell, Copy, Download, Share, Smartphone, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import {
  copyAppUrl,
  getInstallGuidance,
  promptNativeInstall,
  subscribeInstallAvailability,
  type InstallGuidance,
} from "@/lib/pwaInstall";
import { enableJobPushNotifications, JOB_ALERTS_ENABLED_KEY } from "@/lib/jobPushNotifications";

const DISMISS_KEY = "stravo_pwa_install_dismissed_v1";
const ALERTS_DISMISS_KEY = "stravo_job_alerts_dismissed_v1";

type Props = {
  className?: string;
  /** Compact row for menus / account pages */
  compact?: boolean;
  /** When set, Home Screen installs can opt into job push alerts */
  userId?: string | null;
};

function useGuidance(): InstallGuidance {
  const [guidance, setGuidance] = useState(getInstallGuidance);
  useEffect(() => {
    setGuidance(getInstallGuidance());
    return subscribeInstallAvailability(() => setGuidance(getInstallGuidance()));
  }, []);
  return guidance;
}

/**
 * Guides owners + cleaners to Add Stravo to their phone home screen (PWA).
 * On iPhone, Add to Home Screen only appears in Safari — Chrome/Google share sheets omit it.
 * When already installed (standalone), offers job alert push opt-in when VAPID is configured.
 */
export function InstallAppCard({ className, compact, userId }: Props) {
  const guidance = useGuidance();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [alertsDismissed, setAlertsDismissed] = useState(() => {
    try {
      return localStorage.getItem(ALERTS_DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [alertsEnabled, setAlertsEnabled] = useState(() => {
    try {
      return localStorage.getItem(JOB_ALERTS_ENABLED_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [busy, setBusy] = useState(false);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  const dismissAlerts = () => {
    try {
      localStorage.setItem(ALERTS_DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
    setAlertsDismissed(true);
  };

  const onInstall = async () => {
    setBusy(true);
    try {
      await promptNativeInstall();
    } finally {
      setBusy(false);
    }
  };

  const onCopy = async () => {
    const ok = await copyAppUrl();
    toast({
      title: ok ? "Link copied" : "Copy this link",
      description: ok
        ? "Open Safari, paste in the address bar, then Share → Add to Home Screen."
        : "Open Safari and go to portal.stravo.ai, then Share → Add to Home Screen.",
    });
  };

  const onEnableAlerts = async () => {
    if (!userId) return;
    setBusy(true);
    try {
      const result = await enableJobPushNotifications(userId);
      if (result === "ok") {
        try {
          localStorage.setItem(JOB_ALERTS_ENABLED_KEY, "1");
        } catch {
          /* ignore */
        }
        setAlertsEnabled(true);
        toast({ title: "Job alerts on", description: "We’ll notify you when checkout status changes." });
      } else if (result === "denied") {
        toast({ title: "Notifications blocked", description: "Allow notifications in Settings to get job alerts.", variant: "destructive" });
      } else if (result === "no_vapid") {
        toast({ title: "Alerts not configured yet", description: "Install is ready — push keys will be added soon." });
      } else {
        toast({ title: "Not supported", description: "Job alerts need a Home Screen install on a supported phone." });
      }
    } catch (e) {
      toast({
        title: "Couldn’t enable alerts",
        description: e instanceof Error ? e.message : "Try again",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  // Already installed — offer job alerts instead of install guidance.
  if (guidance.kind === "standalone") {
    if (!userId || alertsDismissed || alertsEnabled) return null;

    const alertsBody = (
      <div className="space-y-2">
        <p className={compact ? "text-muted-foreground text-xs leading-relaxed" : "text-[13px] font-medium leading-relaxed text-[#475569]"}>
          Get notified when a guest leaves, Out slips, or you’re waiting past checkout.
        </p>
        <Button
          size={compact ? "sm" : "default"}
          className={compact ? "mt-1" : "bg-[#24a89a] hover:bg-[#1f958a]"}
          onClick={onEnableAlerts}
          disabled={busy}
        >
          <Bell className="mr-1.5 h-3.5 w-3.5" />
          {busy ? "Enabling…" : "Turn on job alerts"}
        </Button>
      </div>
    );

    if (compact) {
      return (
        <div className={cn("rounded-lg border bg-card p-3 text-sm", className)}>
          <div className="flex items-start gap-2">
            <Bell className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <div className="min-w-0 flex-1 space-y-1">
              <p className="font-medium">Job alerts</p>
              {alertsBody}
            </div>
            <button type="button" onClick={dismissAlerts} className="text-muted-foreground hover:text-foreground" aria-label="Dismiss">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      );
    }

    return (
      <div
        className={cn(
          "relative overflow-hidden rounded-[16px] border border-[#d7e0e6] bg-white p-4 shadow-sm",
          className,
        )}
      >
        <button
          type="button"
          onClick={dismissAlerts}
          className="absolute right-3 top-3 text-[#94a3b8] hover:text-[#475569]"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex gap-3 pr-6">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#e7f6f4]">
            <Bell className="h-5 w-5 text-[#15776d]" />
          </div>
          <div className="min-w-0 space-y-2">
            <div>
              <p className="text-[16px] font-bold text-[#0f1720]">Job alerts</p>
              <p className="mt-0.5 text-[13px] font-medium leading-relaxed text-[#64748b]">
                Stay ahead of same-day checkout changes.
              </p>
            </div>
            {alertsBody}
          </div>
        </div>
      </div>
    );
  }

  if (dismissed) return null;

  const body =
    guidance.kind === "ios-open-safari" ? (
      <div className="space-y-2">
        <p className={compact ? "text-muted-foreground text-xs leading-relaxed" : "text-[13px] font-medium leading-relaxed text-[#475569]"}>
          You’re in Chrome/Google. iPhone only shows <strong>Add to Home Screen</strong> in{" "}
          <strong>Safari</strong>.
        </p>
        <ol
          className={cn(
            "list-decimal space-y-1 pl-4",
            compact ? "text-muted-foreground text-xs" : "text-[13px] font-medium text-[#475569]",
          )}
        >
          <li>Tap Share → <strong>Open in Safari</strong> (or copy the link below)</li>
          <li>In Safari, tap <Share className="inline h-3 w-3" /> Share</li>
          <li>Scroll and tap <strong>Add to Home Screen</strong></li>
        </ol>
        <Button size="sm" variant="outline" className="mt-1" onClick={onCopy}>
          <Copy className="mr-1.5 h-3.5 w-3.5" />
          Copy Stravo link
        </Button>
      </div>
    ) : guidance.kind === "ios-safari" ? (
      <p className={compact ? "text-muted-foreground text-xs leading-relaxed" : "text-[13px] font-medium text-[#475569]"}>
        Tap <Share className="inline h-3.5 w-3.5 align-text-bottom" /> Share at the bottom, scroll down, then{" "}
        <strong>Add to Home Screen</strong>.
      </p>
    ) : guidance.kind === "native" ? (
      <Button
        size={compact ? "sm" : "default"}
        className={compact ? "mt-1" : "bg-[#24a89a] hover:bg-[#1f958a]"}
        onClick={onInstall}
        disabled={busy}
      >
        <Download className="mr-1.5 h-3.5 w-3.5" />
        {busy ? "Opening…" : "Add to home screen"}
      </Button>
    ) : (
      <p className={compact ? "text-muted-foreground text-xs leading-relaxed" : "text-[13px] font-medium text-[#475569]"}>
        On your <strong>phone</strong>: open <strong>portal.stravo.ai</strong> in Safari → Share → Add to Home
        Screen. (Desktop Install is optional.)
      </p>
    );

  if (compact) {
    return (
      <div className={cn("rounded-lg border bg-card p-3 text-sm", className)}>
        <div className="flex items-start gap-2">
          <Smartphone className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <div className="min-w-0 flex-1 space-y-1">
            <p className="font-medium">Install Stravo</p>
            {body}
          </div>
          <button type="button" onClick={dismiss} className="text-muted-foreground hover:text-foreground" aria-label="Dismiss">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-[16px] border border-[#d7e0e6] bg-white p-4 shadow-sm",
        className,
      )}
    >
      <button
        type="button"
        onClick={dismiss}
        className="absolute right-3 top-3 text-[#94a3b8] hover:text-[#475569]"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex gap-3 pr-6">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#e7f6f4]">
          <Smartphone className="h-5 w-5 text-[#15776d]" />
        </div>
        <div className="min-w-0 space-y-2">
          <div>
            <p className="text-[16px] font-bold text-[#0f1720]">Use Stravo like an app</p>
            <p className="mt-0.5 text-[13px] font-medium leading-relaxed text-[#64748b]">
              Save it to your phone’s home screen — must use Safari on iPhone.
            </p>
          </div>
          {body}
        </div>
      </div>
    </div>
  );
}
